import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NafathStatusEnums } from '@common/enums/nafath.enum';
import { WathiqStatusEnums } from '@common/enums/wathiq.enum';
import { TranslateService } from '@ngx-translate/core';
import { DashboardService } from '@pages/dashboard/dashboard.service';
import { ErrorHandlingService } from './error-handling.service';
import { ProxyRequestStatus } from '@common/enums/proxy-request-status';
import { InvestorProfile } from '@common/interfaces/investor-profile';
import { catchError, filter, Observable, of, switchMap, take } from 'rxjs';
import { AMLStatusEnum } from '@common/enums/aml.enum';
import { FirebaseRemoteConfigService } from './firebase-remote-config.service';

@Injectable({
  providedIn: 'root',
})
export class KycVersionsService {
  http = inject(HttpClient);
  translate = inject(TranslateService);
  dashboardService = inject(DashboardService);
  errorHandlingService = inject(ErrorHandlingService);
  // wathiq_rollback - Inject FirebaseRemoteConfigService for centralized wathiq flow status
  firebaseRemoteConfigService = inject(FirebaseRemoteConfigService);

  checkKycVersionsNavigation(): Observable<boolean> {
    return this.dashboardService.investorProfile.pipe(
      filter(profile => profile !== undefined),
      take(1),
      switchMap(profile => {
        return of(this.checkKycStatus(profile as InvestorProfile));
      }),
      catchError(error => {
        this.errorHandlingService.showError(error);
        return of(false);
      })
    );
  }

  checkKycStatus(investorProfile: InvestorProfile | null): boolean {
    if (!investorProfile || !investorProfile.investor) {
      return false;
    }

    if (
      investorProfile?.investor?.type === 1 &&
      investorProfile?.investor?.nafathRequestStatus !==
        NafathStatusEnums?.COMPLETED &&
      investorProfile?.investor?.nafathRequestStatus !==
        NafathStatusEnums?.DEPRECATED
    ) {
      this.errorHandlingService.showError({
        message: this.translate.instant(
          'common_nafath_status_not_completed_error'
        ),
      });
      return false;
      // wathiq_rollback - Wrap wathiq check with centralized turn_on_watheeq_flow
    } else if (
      this.firebaseRemoteConfigService.turn_on_watheeq_flow() &&
      investorProfile?.investor?.type === 2
    ) {
      const wathiqStatus = investorProfile?.investor?.wathqStatus;

      // wathiq_rollback - Only check wathiq status if wathiq flow is enabled
      if (wathiqStatus !== WathiqStatusEnums?.COMPLETED) {
        this.errorHandlingService.showError({
          message: this.translate.instant(
            'common_wathiq_status_not_completed_error'
          ),
        });
        return false;
      }
    } else if (
      investorProfile?.investor?.proxyRequestStatus ===
      ProxyRequestStatus.PENDING_REVIEW
    ) {
      this.errorHandlingService.showError({
        message: this.translate.instant(
          'account_screen_authorized_investor_pending_review_wakala_error'
        ),
      });
      return false;
    } else if (
      investorProfile?.investor?.proxyRequestStatus ===
      ProxyRequestStatus.PENDING_SUBMISSION
    ) {
      this.errorHandlingService.showError({
        message: this.translate.instant(
          'account_screen_authorized_investor_pending_submission_wakala_error'
        ),
      });
      return false;
    } else if (
      investorProfile.investor.amlStatus === AMLStatusEnum.PENDING_REVIEW
    ) {
      this.errorHandlingService.showError({
        message: this.translate.instant(
          'home_screen_kyc_pending_review_warning_title'
        ),
      });
      return false;
    } else if (investorProfile.investor.amlStatus === AMLStatusEnum.FAILED) {
      this.errorHandlingService.showError({
        message: this.translate.instant(
          'common_aml_failed_warning_description'
        ),
      });
      return false;
    }

    return true;
  }
}
